import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { useState, useMemo, useEffect } from "react";
import Modal from "../../layouts/Modal";
import {
  useForm,
  UseFormRegister,
  UseFormSetValue,
  FieldValues,
} from "react-hook-form";
import Papa from "papaparse";
import SelectInput from "./Inputs/SelectInput";
import { UseMutationResult } from "@tanstack/react-query";
import classNames from "../../utils/classNames";
import LoadingWheel from "./LoadingWheel";

interface ImportFromCsvProps<T extends Record<string, any>> {
  /**
   * A sample object whose keys define the target form properties.
   * Use its keys for mapping and its values to infer basic parsing.
   */
  prototypeObject: T;

  /**
   * React Query mutation that accepts an array of T
   */
  mutation?: UseMutationResult<any, unknown, T[], unknown>;

  onSuccess?: (data: T[]) => void;

  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ImportFromCsv<
  T extends Record<string, any>
>({ prototypeObject, mutation, onSuccess, open, setOpen }: ImportFromCsvProps<T>) {
  const [options, setOptions] = useState<{ id: any; name: string }[]>([{ id: undefined, name: "-- Ignore --"}]);
  const [loadingModal, setLoadingModal] = useState(false);
  const [csvData, setCsvData] = useState<Record<string, string>[]>([]);

  // derive form fields from keys of prototypeObject
  const formFields = useMemo(() => {
    return Object.keys(prototypeObject).map((key, index) => ({
      id: index,
      name: key as keyof T,
      label: tokenToLabel(key),
      type: typeof prototypeObject[key as keyof T],
    }));
  }, [prototypeObject]);

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    reset,
  } = useForm<Record<keyof T, string>>();

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const firstRow = results.data[0] || {};
        setOptions([
          { id: undefined, name: "-- Ignore --"},
          ...(results.meta?.fields || []).map((field, index) => ({
            id: field,
            name: `${tokenToLabel(field)} - ${firstRow[field] ?? ""}`,
          }))
        ]);
        setCsvData(results.data);
        reset();
      },
    });
  };

  const onSubmit = (mapping: Record<keyof T, string>) => {
    let first = true;
    const transformed: T[] = csvData.map((row) => {
      const obj = {} as T;
      formFields.forEach(({ name, type }) => {
        const col = mapping[name];

        if (!col) return; // skip if no mapping

        if (first) {
          console.log(`Mapping ${col}`, row);
          first = false;
        }

        let val: any = row[col];

        // basic parsing for boolean and number
        if (type === "boolean") {
          val = String(val).toLowerCase() === "true";
        } else if (type === "number") {
          const n = Number(val);
          val = Number.isNaN(n) ? val : n;
        }
        obj[name] = val;
      });
      return obj;
    });

    if(mutation) {
      mutation.mutate(transformed, {
        onSuccess: () => {
          setOpen(false);
          setOptions([]);
          setCsvData([]);
          reset();
          onSuccess?.(transformed);
        },
        onError: (error) => {
          console.error("Import failed:", error);
        },
      });
    } else {
      if (onSuccess) {
        setLoadingModal(true);
        onSuccess(transformed); // if no mutation, give the data back to the caller to handle it
      }
    }
  };

  useEffect(() => {
    if (open) return;
    
    // reset state when modal closes
    setLoadingModal(false);
    setOptions([{ id: undefined, name: "-- Ignore --"}]);
    setCsvData([]);
    reset();

  }, [open]);

  return (
    <>
      <button
        className="bg-primary-600 hover:bg-primary-700 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
        type="button"
        onClick={() => setOpen(true)}
      >
        <DocumentTextIcon className="h-5 w-5 mr-2" />
        Import from CSV
      </button>

      <Modal open={loadingModal} setOpen={() => {}} width="max-w-xl">
        <LoadingWheel />
      </Modal>

      <Modal open={open} setOpen={setOpen} width="max-w-4xl">
        <>
          <h1 className="text-xl font-semibold">Import from CSV</h1>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4 sm:gap-y-8"
          >
            <div className="sm:col-span-2 mt-4">
              <input 
                className="block w-full text-gray-700 border border-gray-300 rounded-md shadow-sm
                  file:py-2 file:px-4
                  file:border-0
                  file:rounded-l-md
                  file:bg-primary-600
                  file:text-white
                  file:font-semibold
                  file:mr-4
                  hover:file:bg-primary-700
                "
                onChange={handleFileUpload}
                id="file_input" 
                type="file"
                accept=".csv"
              />
            </div>

            <hr className="sm:col-span-2" />

            <h2 className="text-lg font-semibold sm:col-span-2">Mappings</h2>

            {formFields.map((field, idx) => {
              return (
                <SelectInput
                  key={idx}
                  label={field.label}
                  register={register}
                  registerName={field.name as string}
                  setValue={setValue}
                  watch={watch}
                  options={options}
                  selectedClass="!border-primary-600 !bg-primary-50"
                />
              );
            })}

            <div className="sm:col-span-2 flex justify-end mt-4">
              <button
                type="submit"
                className="bg-primary-600 hover:bg-primary-700 rounded-md px-4 py-2 text-white"
              >
                Import
              </button>
            </div>
          </form>
        </>
      </Modal>
    </>
  );
}

/** Splits camelCase or snake_case into Title Case */
function tokenToLabel(token: string) {
  return token
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
